function sendUrlMessage(url) {
  // Simple deduplication based on URL and timestamp
  const now = Date.now();
  const urlKey = url;
  
  // Check if we've sent this URL recently (within 500ms)
  if (window._lastUrlSent && window._lastUrlSent.url === urlKey && (now - window._lastUrlSent.timestamp) < 500) {
    console.log('[IframeMessaging] Skipping duplicate URL message:', url);
    return;
  }
  
  window._lastUrlSent = { url: urlKey, timestamp: now };
  
  window.parent.postMessage({
    type: 'save_data',
    'opening-url': url
  }, '*');
}

function sendWordClickMessage(wordData) {
  window.parent.postMessage({
    type: 'word_click',
    data: wordData
  }, '*');
}

function sendWordModalOpenMessage(modalData) {
  window.parent.postMessage({
    type: 'word_modal_open',
    data: modalData
  }, '*');
}

function sendWordModalCloseMessage() {
  window.parent.postMessage({
    type: 'word_modal_close',
    data: { timestamp: Date.now() }
  }, '*');
}

function sendWordHighlightMessage(wordData) {
  window.parent.postMessage({
    type: 'word_highlight',
    data: wordData
  }, '*');
}

function sendPageScrollMessage(pageData) {
  window.parent.postMessage({
    type: 'page_scroll',
    data: pageData
  }, '*');
}

export function notifyParentOfUrlChange(url = window.location.href) {
  if (window.parent !== window) {
    sendUrlMessage(url);
  }
}

export function notifyParentOfWordClick(wordData) {
  if (window.parent !== window) {
    sendWordClickMessage(wordData);
  }
}

export function notifyParentOfWordModalOpen(modalData) {
  if (window.parent !== window) {
    sendWordModalOpenMessage(modalData);
  }
}

export function notifyParentOfWordModalClose() {
  if (window.parent !== window) {
    sendWordModalCloseMessage();
  }
}

export function notifyParentOfWordHighlight(wordData) {
  if (window.parent !== window) {
    sendWordHighlightMessage(wordData);
  }
}

export function notifyParentOfPageScroll(pageData) {
  if (window.parent !== window) {
    sendPageScrollMessage(pageData);
  }
}

function handleWordClick(wordData) {
  // For word clicks, we should open the modal instead of just highlighting
  const location = wordData.location;
  const wordElement = document.querySelector(`[data-location="${location}"]`);
  if (wordElement) {
    console.log('[IframeMessaging] Triggering word modal open for:', location);
    // Simulate a click on the word element to trigger the modal
    const clickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true
    });
    wordElement.dispatchEvent(clickEvent);
  }
}

function handleWordModalOpen(modalData) {
  const location = modalData.location;
  const wordElement = document.querySelector(`[data-location="${location}"]`);
  if (wordElement && modalData.word) {
    console.log('[IframeMessaging] Opening word modal programmatically:', modalData);
    openWordModal(modalData.word, modalData.location);
  }
}

function handleWordModalClose() {
  console.log('[IframeMessaging] Closing word modal programmatically');
  closeWordModal();
}

// Helper functions for modal operations
function openWordModal(word, location) {
  const modal = document.getElementById("word-modal");
  const modalContent = document.getElementById("word-modal-content");
  const backdrop = document.getElementById("word-modal-backdrop");
  const loading = modal.querySelector("#loading");
  
  if (!modal || !modalContent || !backdrop || !loading) {
    console.log('[IframeMessaging] Modal elements not found, cannot open modal');
    return;
  }

  // Setup close button listener
  const closeBtn = modal.querySelector(".close-modal");
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      closeWordModal();
      notifyParentOfWordModalClose();
    });
  }

  modal.classList.remove("hidden");
  backdrop.classList.remove("hidden");
  document.body.classList.add("overflow-hidden");

  void modal.offsetWidth;

  modal.classList.add("flex");
  backdrop.classList.replace("opacity-0", "opacity-100");
  modalContent.classList.replace("scale-95", "scale-100");
  modalContent.classList.replace("opacity-0", "opacity-100");

  // Fetch word data
  const fetchOptions = {
    method: 'GET',
    headers: {
      Accept: 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml'
    }
  };

  fetch(`/words/${word}`, fetchOptions)
    .then(response => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.text();
    })
    .then(html => {
      const turbo = window.Turbo || (window.Stimulus && window.Stimulus.application && window.Stimulus.application.turbo);
      if (turbo && turbo.renderStreamMessage) {
        turbo.renderStreamMessage(html);
      }
      loading.classList.add("hidden");
    })
    .catch(error => console.error('Word modal fetch error:', error));
}

function closeWordModal() {
  const modal = document.getElementById("word-modal");
  const modalContent = document.getElementById("word-modal-content");
  const backdrop = document.getElementById("word-modal-backdrop");
  
  if (!modal || !modalContent || !backdrop) return;

  backdrop.classList.replace("opacity-100", "opacity-0");
  modalContent.classList.replace("scale-100", "scale-95");
  modalContent.classList.replace("opacity-100", "opacity-0");
  document.body.classList.remove("overflow-hidden");
  
  const wordContent = document.getElementById('word_content');
  if (wordContent) {
    wordContent.innerText = '';
  }
  
  const loading = modal.querySelector("#loading");
  if (loading) {
    loading.classList.remove("hidden");
  }

  setTimeout(() => {
    modal.classList.add("hidden");
    backdrop.classList.add("hidden");
    modal.classList.remove("flex");
  }, 300);
}

function handleWordHighlight(wordData) {
  const location = wordData.location;
  const wordElement = document.querySelector(`[data-location="${location}"]`);
  if (wordElement) {
    document.querySelectorAll('.highlighted-word').forEach(el => {
      el.classList.remove('highlighted-word');
    });
    wordElement.classList.add('highlighted-word');
    console.log('[IframeMessaging] Applied hover highlight to word:', location);
  }
}

function handlePageScroll(pageData) {
  const { chapter, ayah } = pageData;
  if (chapter && ayah) {
    const ayahElement = document.querySelector(`[data-ayah="${chapter}:${ayah}"]`);
    if (ayahElement) {
      ayahElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
}

function setupWordHighlightListeners() {
  console.log('[IframeMessaging] Setting up word click and highlight listeners');
  
  // Check if already set up
  if (document._wordListenersSetup) {
    console.log('[IframeMessaging] Word listeners already set up, skipping...');
    return;
  }
  document._wordListenersSetup = true;
  
  // Handle word clicks
  document.addEventListener('click', (event) => {
    const wordElement = event.target.closest('[data-location]');
    console.log('[IframeMessaging] Click detected:', wordElement?.dataset);
    
    if (wordElement && wordElement.dataset.char === 'word') {
      const wordData = {
        location: wordElement.dataset.location,
        word: wordElement.dataset.word,
        ayah: wordElement.closest('[data-ayah]')?.dataset.ayah,
        type: 'click',
        timestamp: Date.now()
      };
      console.log('[IframeMessaging] Sending word click:', wordData);
      notifyParentOfWordClick(wordData);
    }
  });

  // Handle word hover using mouseenter/mouseleave (better than mouseover/mouseout)
  let lastHoveredElement = null;
  
  document.addEventListener('mouseenter', (event) => {
    const wordElement = event.target.closest('[data-location]');
    if (wordElement && wordElement.dataset.char === 'word') {
      // Only send if it's a different word element
      if (lastHoveredElement === wordElement) return;
      
      lastHoveredElement = wordElement;
      const wordData = {
        location: wordElement.dataset.location,
        word: wordElement.dataset.word,
        ayah: wordElement.closest('[data-ayah]')?.dataset.ayah,
        type: 'hover',
        timestamp: Date.now()
      };
      console.log('[IframeMessaging] Sending word highlight (hover):', wordData);
      notifyParentOfWordHighlight(wordData);
    }
  }, true); // Use capture phase

  // Clear hover state when mouse leaves
  document.addEventListener('mouseleave', (event) => {
    const wordElement = event.target.closest('[data-location]');
    if (wordElement && wordElement.dataset.char === 'word') {
      if (lastHoveredElement === wordElement) {
        lastHoveredElement = null;
      }
    }
  }, true); // Use capture phase
}

function setupPageScrollListener() {
  console.log('[IframeMessaging] Setting up page scroll listener');
  
  // Check if already set up
  if (document._scrollListenersSetup) {
    console.log('[IframeMessaging] Scroll listeners already set up, skipping...');
    return;
  }
  document._scrollListenersSetup = true;
  
  let scrollTimeout;
  
  window.addEventListener('scroll', () => {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      const visibleAyahs = Array.from(document.querySelectorAll('[data-ayah]')).filter(el => {
        const rect = el.getBoundingClientRect();
        return rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 2;
      });

      console.log('[IframeMessaging] Scroll detected, visible ayahs:', visibleAyahs.length);

      if (visibleAyahs.length > 0) {
        const centerAyah = visibleAyahs[0];
        const ayahKey = centerAyah.dataset.ayah;
        if (ayahKey) {
          const [chapter, ayah] = ayahKey.split(':');
          const pageData = {
            chapter: parseInt(chapter),
            ayah: parseInt(ayah),
            ayahKey: ayahKey,
            scrollTop: window.pageYOffset,
            timestamp: Date.now()
          };
          console.log('[IframeMessaging] Sending page scroll:', pageData);
          notifyParentOfPageScroll(pageData);
        }
      }
    }, 250);
  });

  document.addEventListener('turbo:load', () => {
    setTimeout(() => {
      const firstAyah = document.querySelector('[data-ayah]');
      if (firstAyah) {
        const ayahKey = firstAyah.dataset.ayah;
        const [chapter, ayah] = ayahKey.split(':');
        const pageData = {
          chapter: parseInt(chapter),
          ayah: parseInt(ayah),
          ayahKey: ayahKey,
          scrollTop: 0,
          type: 'page_load',
          timestamp: Date.now()
        };
        notifyParentOfPageScroll(pageData);
      }
    }, 100);
  });
}

function setupMessageListener() {
  // Check if already set up
  if (document._messageListenersSetup) {
    console.log('[IframeMessaging] Message listeners already set up, skipping...');
    return;
  }
  document._messageListenersSetup = true;
  
  window.addEventListener('message', (event) => {
    if (event.source !== window.parent) return;
    
    switch (event.data.type) {
      case 'word_click':
        handleWordClick(event.data.data);
        break;
      case 'word_modal_open':
        handleWordModalOpen(event.data.data);
        break;
      case 'word_modal_close':
        handleWordModalClose();
        break;
      case 'word_highlight':
        handleWordHighlight(event.data.data);
        break;
      case 'page_scroll':
        handlePageScroll(event.data.data);
        break;
    }
  });
}

let isInitialized = false;

export function initializeIframeMessaging() {
  console.log('[IframeMessaging] Initializing iframe messaging...');
  console.log('[IframeMessaging] Is in iframe:', window.parent !== window);
  
  if (window.parent === window) {
    console.log('[IframeMessaging] Not in iframe, skipping initialization');
    return;
  }

  if (isInitialized) {
    console.log('[IframeMessaging] Already initialized, skipping');
    return;
  }

  isInitialized = true;

  console.log('[IframeMessaging] Setting up URL change listeners');
  // Send initial URL on load
  notifyParentOfUrlChange();

  // Listen for Turbo navigation events - only use turbo:load to avoid duplicates
  document.addEventListener('turbo:load', () => {
    console.log('[IframeMessaging] Turbo load event');
    notifyParentOfUrlChange();
    
    // Re-setup listeners after Turbo load (in case DOM changed)
    setTimeout(() => {
      console.log('[IframeMessaging] Re-checking elements after Turbo load');
      const wordElements = document.querySelectorAll('[data-location]');
      const ayahElements = document.querySelectorAll('[data-ayah]');
      console.log('[IframeMessaging] Found word elements:', wordElements.length);
      console.log('[IframeMessaging] Found ayah elements:', ayahElements.length);
    }, 100);
  });

  // Listen for browser back/forward navigation
  window.addEventListener('popstate', () => {
    notifyParentOfUrlChange();
  });

  // Setup word highlighting and page scroll listeners
  setupWordHighlightListeners();
  setupPageScrollListener();
  setupMessageListener();
  
  // Initial check for elements
  setTimeout(() => {
    const wordElements = document.querySelectorAll('[data-location]');
    const ayahElements = document.querySelectorAll('[data-ayah]');
    console.log('[IframeMessaging] Initial check - word elements:', wordElements.length);
    console.log('[IframeMessaging] Initial check - ayah elements:', ayahElements.length);
  }, 500);
}